import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, breakpoints, Columns, Column, Inline } from '@etg/wings';
import { variants, DangerousHtmlContent, LoadingDots } from '@eti/components';
import { css, cx } from '@eti/styles';
import { useProperty, useTranslation, useSnowplowTracking } from '@eti/providers';
import { checkInBaggage, CheckInBaggagePersuasionModal } from '@eti/products';
import { getFormValues, submit, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { analytics } from '@eti/utils';
import { hash } from '../../../utils/hash';
import scrollToErrorMarker from '../utils/validationMessage';
import SubUniqueSellingPoint from '../../../common/components/SubUniqueSellingPoint';
import availablePaymentMethods from '../utils/availablePaymentMethods';
import { routes } from '../../../constants/routesAndApisConstants';
import { CONTACT_INFO_FORM_SECTION, contactFields, contactTypes } from '../constants/formFields';
import {
  MANUALLY_REMOVED_PRODUCTS,
  MANUALLY_ADDED_PRODUCTS,
} from '../../../constants/brandedFareConstants';
import PaymentLogoList from './PaymentLogoList';

const { ARRANGER_FIRST_NAME, ARRANGER_LAST_NAME, CONTACT_TYPE } = contactFields;

const wrapperStyles = css`
  text-align: center;

  &:last-child {
    border-bottom: none;
  }
`;

const submitButtonStyles = css`
  margin: 16px auto;
  width: 100%;

  @media (min-width: 48em) {
    max-width: 250px;
  }
`;

const confirmationStyle = css`
  font-size: 0.875rem;
  margin-bottom: 16px;
`;

const protectUSPStyles = css`
  align-items: center;
`;

const inlineCardStyles = (isUSPEnabled) => css`
  justify-content: center;

  @media (min-width: ${breakpoints._560}) {
    justify-content: ${isUSPEnabled ? 'end' : 'center'};
  }
`;

const cardStyles = css`
  padding: 0 15px;
`;

const TravelerDetailsBottomContent = ({
  checkInBaggageProduct,
  customRef,
  emailAddress,
  form,
  formValues,
  hasSecondaryProducts = false,
  isBookingStepLastPage = false,
  isFormValid,
  isLoading = false,
  isResponsiveFlow = false,
  selectedTravelerContactType,
  showSeatMapModal = () => {},
  shouldShowSeatMapModal = false,
  shouldApplySelectionToAll,
  travelArrangerFirstName,
  travelArrangerLastName,
  wizardSteps,
  handleRevealNextSection,
  submitForm,
  setShowInfoReviewModal,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const history = useHistory();
  const [isCheckInBaggagePersuasionModalOpen, setIsCheckInBaggagePersuasionModalOpen] =
    useState(false);
  const isContactInformationInPage =
    history.location.pathname === routes.TRAVELER_DETAILS ||
    history.location.pathname === routes.STEP_TWO;

  const isBaggagePopupEnabled = p('IbeClient.TravelerDetails.BaggagePopupContinue.Enabled');
  const isDynamicBookingFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled');
  const isPrimarySecondaryEnabled = p('TravelerDetails.ProductsPrimarySecondary.Enabled');
  const isWizardEnabled = p('TravelerDetails.ProductsPrimarySecondary.Wizard.Enabled');
  const isPrimarySecondaryEnabledAndWizardEnabled = isPrimarySecondaryEnabled && isWizardEnabled;
  const isInfoReviewModalEnabled = p('TravelerDetails.InfoReviewModal.Enabled');

  const isRFLastSection =
    !isPrimarySecondaryEnabledAndWizardEnabled ||
    (isPrimarySecondaryEnabledAndWizardEnabled && wizardSteps?.length > 2);

  const isDBFLastStep = isDynamicBookingFlowEnabled && isBookingStepLastPage;

  const isOnLastSection = () => {
    // Responsive flow
    if (!isDynamicBookingFlowEnabled) {
      return isRFLastSection;
    }

    // DBF
    if (isDynamicBookingFlowEnabled && !isResponsiveFlow) {
      return isDBFLastStep;
    }

    // RF with DBF enabled, we get this in cases like Branded Fares or Flexible ticket.
    if (isDynamicBookingFlowEnabled && isResponsiveFlow) {
      // We don't handle this scenario currently, as an example Review modal does NOT appear in this case.
      return true;
    }

    return false;
  };

  const buttonText =
    isInfoReviewModalEnabled || !isOnLastSection()
      ? t('TravelerDetails.BottomContent.ReviewEnable.ProceedButton.Text')
      : t('TravelerDetails.BottomContent.buttonText');

  useEffect(() => {
    return () => {
      setIsCheckInBaggagePersuasionModalOpen(false);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const handleSendTrackEvent = (action, label) => {
      if (isSnowplowTrackingEnabled) {
        const category = 'Traveler Details';
        sendTrackEvent({
          category,
          action,
          label,
        });
      }
    };
    if (selectedTravelerContactType === contactTypes.ARRANGER) {
      sessionStorage.setItem('travelArrangerFirstName', travelArrangerFirstName);
      sessionStorage.setItem('travelArrangerLastName', travelArrangerLastName);
    }
    if (selectedTravelerContactType === contactTypes.TRAVELER) {
      sessionStorage.removeItem('travelArrangerFirstName');
      sessionStorage.removeItem('travelArrangerLastName');
    }

    if (!isFormValid) {
      await submitForm();
      scrollToErrorMarker(true, isPrimarySecondaryEnabledAndWizardEnabled);
      requestAnimationFrame(() => {
        const firstError = document.querySelector('[aria-invalid="true"]');

        if (firstError && typeof firstError.focus === 'function' && firstError.tabIndex >= 0) {
          firstError.focus({ preventScroll: true });
          return;
        }

        const focusableChild = firstError?.querySelector(
          'input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (focusableChild && typeof focusableChild.focus === 'function') {
          focusableChild.focus({ preventScroll: true });
        }
      });
      handleSendTrackEvent('Error', 'Validation Error');
      return;
    }
    if (shouldShowSeatMapModal) {
      showSeatMapModal(
        !isDynamicBookingFlowEnabled && !isRFLastSection && hasSecondaryProducts
          ? handleRevealNextSection
          : submitForm,
      );
      handleSendTrackEvent('Click', 'Continue button');
      return;
    }
    if (!isDynamicBookingFlowEnabled && !isRFLastSection && hasSecondaryProducts) {
      handleRevealNextSection();
      handleSendTrackEvent('Click', 'Continue button');
      return;
    }
    if (isInfoReviewModalEnabled && isOnLastSection()) {
      setShowInfoReviewModal(true);
      return;
    }
    analytics.pushEntryToDataLayer({
      event: 'user_email_added',
      user_email: await hash.withSHA256(emailAddress),
    });
    if (isPrimarySecondaryEnabledAndWizardEnabled && wizardSteps?.length > 2) {
      sessionStorage.setItem(
        'documentReferrer',
        `${window.location.origin}/rf/traveler-details-extra-services`,
      );
    }
    if (
      isContactInformationInPage &&
      isFormValid &&
      isBaggagePopupEnabled &&
      checkInBaggageProduct &&
      !Object.prototype.hasOwnProperty.call(formValues, checkInBaggage.name)
    ) {
      setIsCheckInBaggagePersuasionModalOpen(true);
      return;
    }

    localStorage.removeItem(MANUALLY_REMOVED_PRODUCTS);
    localStorage.removeItem(MANUALLY_ADDED_PRODUCTS);
    submitForm();
  };

  let filteredPaymentMethods = [];
  const sortOrders = [
    ...new Set(p('IbeClient.TravelerDetails.PaymentMethodsDisplay.SortOrder')),
  ].slice(0, 5);
  if (sortOrders && sortOrders.length > 0) {
    const paymentMethods = sortOrders.map((code) => availablePaymentMethods[code]);
    filteredPaymentMethods = paymentMethods.filter((method) => method !== undefined);
  }

  const creditCardWidth = {
    [breakpoints._0]: '4/4',
    [breakpoints._560]: p('IbeClient.USP.Enabled') ? '4/6' : '6/6',
  };

  const titles = {
    firstTitle: t('TravelerDetails.USPOne.Label'),
    secondTitle: t('TravelerDetails.USPTwo.Label'),
    thirdTitle: t('TravelerDetails.USPThree.Label'),
  };

  const isPaymentMethodLogosEnabled = p('IbeClient.ButtonLockUSPPaymentMethods.Enabled');
  const buttonId = 'bookNow-button';

  return (
    <>
      {isCheckInBaggagePersuasionModalOpen && (
        <CheckInBaggagePersuasionModal
          form={form}
          order={checkInBaggageProduct?.order}
          shouldApplySelectionToAll={shouldApplySelectionToAll}
        />
      )}
      <div className={wrapperStyles} data-testid="travelerDetails-bottom-content">
        <DangerousHtmlContent
          className={cx(confirmationStyle, 'confirmation-block')}
          content={t('TravelerDetails.BottomContent.confirmation')}
          customRef={customRef}
        />
        {p('IbeClient.USP.Enabled') ? (
          <Columns className={protectUSPStyles} collapseBelow={breakpoints._560}>
            <Column width={{ [breakpoints._0]: '4/4', [breakpoints._560]: '4/6' }}>
              <SubUniqueSellingPoint titles={titles} />
            </Column>
            <Column width={{ [breakpoints._0]: '4/4', [breakpoints._560]: '2/6' }}>
              <Button
                className={submitButtonStyles}
                data-testid={buttonId}
                id={buttonId}
                onClick={handleSubmit}
                variant={variants.PRIMARY}
              >
                {isLoading ? <LoadingDots dataTestId="travelerDetails-loadingDots" /> : buttonText}
              </Button>
            </Column>
          </Columns>
        ) : (
          <Button
            className={submitButtonStyles}
            data-testid={buttonId}
            id={buttonId}
            onClick={handleSubmit}
            variant={variants.PRIMARY}
          >
            {isLoading ? <LoadingDots dataTestId="travelerDetails-loadingDots" /> : buttonText}
          </Button>
        )}
        {isPaymentMethodLogosEnabled && filteredPaymentMethods.length > 1 && (
          <Columns className={cardStyles} data-testid="payment-method-logos">
            <Column width={creditCardWidth}>
              <Inline className={inlineCardStyles(p('IbeClient.USP.Enabled'))}>
                <PaymentLogoList methods={filteredPaymentMethods} />
              </Inline>
            </Column>
          </Columns>
        )}
      </div>
    </>
  );
};

TravelerDetailsBottomContent.propTypes = {
  checkInBaggageProduct: PropTypes.objectOf(PropTypes.any),
  customRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  emailAddress: PropTypes.string,
  form: PropTypes.string,
  formValues: PropTypes.object,
  handleRevealNextSection: PropTypes.func,
  hasSecondaryProducts: PropTypes.bool,
  isBookingStepLastPage: PropTypes.bool,
  isFormValid: PropTypes.bool,
  isLoading: PropTypes.bool,
  isResponsiveFlow: PropTypes.bool,
  selectedTravelerContactType: PropTypes.string,
  setShowInfoReviewModal: PropTypes.func,
  shouldApplySelectionToAll: PropTypes.bool,
  shouldShowSeatMapModal: PropTypes.bool,
  showSeatMapModal: PropTypes.func,
  submitForm: PropTypes.func,
  travelArrangerFirstName: PropTypes.string,
  travelArrangerLastName: PropTypes.string,
  wizardSteps: PropTypes.arrayOf(PropTypes.number),
};

const mapStateToProps = (state, { form }) => {
  const productName = checkInBaggage.name;
  const shouldApplySelectionToAll = formValueSelector(form)(
    state,
    `${productName}.shouldApplySelectionToAll`,
  );
  return {
    formValues: getFormValues(form)(state),
    shouldApplySelectionToAll: shouldApplySelectionToAll ?? true,
    selectedTravelerContactType: formValueSelector(form)(
      state,
      `${CONTACT_INFO_FORM_SECTION}.${CONTACT_TYPE}`,
    ),
    travelArrangerFirstName: formValueSelector(form)(
      state,
      `${CONTACT_INFO_FORM_SECTION}.${ARRANGER_FIRST_NAME}`,
    ),
    travelArrangerLastName: formValueSelector(form)(
      state,
      `${CONTACT_INFO_FORM_SECTION}.${ARRANGER_LAST_NAME}`,
    ),
  };
};

const mapDispatchToProps = (dispatch, { form }) => ({
  submitForm() {
    dispatch(submit(form));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TravelerDetailsBottomContent);
